/* Desktop compact header. The legacy handler owns screens below 1200px. */
(function initDesktopHeader() {
  const root = document.querySelector('.site-header');
  if (!root) return;

  const header = root.querySelector('.header');
  const utility = root.querySelector('.site-header__utility');
  const placeholder = root.nextElementSibling;
  const field = root.querySelector('.ui-field--header');
  if (!header || !utility || !placeholder?.classList.contains('header-placeholder')) return;

  const desktop = window.matchMedia('(min-width: 1200px)');
  let compact = false;
  let expandedHeight = 0;
  let threshold = 0;
  let frame = 0;

  function update() {
    frame = 0;
    if (!desktop.matches) return;
    const nextCompact = window.scrollY >= threshold;
    if (compact === nextCompact) return;
    compact = nextCompact;
    root.classList.toggle('site-header--compact', compact);
    field?.classList.toggle('ui-field--small', compact);
    placeholder.style.height = compact ? expandedHeight + 'px' : '';
    placeholder.classList.toggle('is-active', compact);
  }

  function measure() {
    if (!desktop.matches) {
      if (compact) {
        root.classList.remove('site-header--compact');
        field?.classList.remove('ui-field--small');
        placeholder.style.height = '';
        placeholder.classList.remove('is-active');
        compact = false;
      }
      return;
    }
    // Measure the expanded rows before restoring compact state in this frame.
    root.classList.remove('site-header--compact');
    field?.classList.remove('ui-field--small');
    placeholder.style.height = '';
    placeholder.classList.remove('is-active');
    expandedHeight = header.getBoundingClientRect().height;
    threshold = utility.getBoundingClientRect().bottom + window.scrollY;
    compact = false;
    update();
  }

  window.addEventListener('scroll', function () {
    if (!frame) frame = window.requestAnimationFrame(update);
  }, { passive: true });
  window.addEventListener('resize', measure);
  window.addEventListener('pageshow', measure);
  window.addEventListener('load', measure);
  document.fonts?.ready.then(measure);
  measure();
})();

/* Desktop disclosure menus. Below 1200px initMobileMenu owns the menu. */
(function initDesktopMenus() {
  const desktop = window.matchMedia('(min-width: 1200px)');
  const items = Array.from(document.querySelectorAll('.site-header .has-megamenu'));
  let active = null;
  let closeTimer = 0;
  let restoringFocus = false;

  function cancelClose() {
    window.clearTimeout(closeTimer);
    closeTimer = 0;
  }

  function close() {
    cancelClose();
    if (!active) return;
    active.classList.remove('site-header__menu-open');
    active.querySelector(':scope > a').setAttribute('aria-expanded', 'false');
    active = null;
  }

  function open(item) {
    cancelClose();
    if (!desktop.matches || restoringFocus || active === item) return;
    close();
    active = item;
    item.classList.add('site-header__menu-open');
    item.querySelector(':scope > a').setAttribute('aria-expanded', 'true');
  }

  for (const item of items) {
    const trigger = item.querySelector(':scope > a');
    // Alleen een muis opent het menu bij aanwijzen. Op een touchscreen opende de
    // eerste tik het menu en navigeerde hij tegelijk.
    item.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'mouse') open(item);
    });
    item.addEventListener('pointerleave', () => {
      if (!desktop.matches || active !== item) return;
      cancelClose();
      closeTimer = window.setTimeout(close, 180);
    });
    item.addEventListener('focusin', () => open(item));
    item.addEventListener('focusout', (event) => {
      if (desktop.matches && active === item && !item.contains(event.relatedTarget)) close();
    });
    trigger.addEventListener('keydown', (event) => {
      if (!desktop.matches || event.key !== 'Enter' || active === item) return;
      event.preventDefault();
      open(item);
    });
    item.querySelector(':scope > .nav-dropdown-indicator')?.addEventListener('click', () => {
      if (!desktop.matches) return;
      if (active === item) close();
      else open(item);
    });
  }

  document.addEventListener('keydown', (event) => {
    if (!desktop.matches || event.key !== 'Escape' || !active) return;
    event.preventDefault();
    const trigger = active.querySelector(':scope > a');
    close();
    // Focus restoration must not reopen the disclosure in its focusin handler.
    restoringFocus = true;
    trigger.focus();
    restoringFocus = false;
  });
  desktop.addEventListener('change', close);
})();

/* Mobile and tablet drill-down menu (<1200px), frames E2 and E3. The panel in
   snippets/base-panel.liquid covers the page; rows with children open the next
   level, the back button returns to the previous one and focuses the row that
   opened it. Focus stays inside the open panel; Esc, the X and the backdrop
   close it and return focus to the menu button.
   The menu is a manual popover: showPopover() moves it into the top layer, so
   the Klaviyo onsite form teaser (injected at runtime by klaviyo-consent-loader.js,
   with its own z-index) cannot cover it, and the teaser itself is left untouched.
   Browsers without popover support fall back to the z-index in the CSS. */
(function initMobileMenu() {
  const menu = document.querySelector('.js-site-menu');
  const toggle = document.querySelector('.site-header .js-header-toggle');
  if (!menu || !toggle) return;

  const panel = menu.querySelector('.ui-panel');
  const body = menu.querySelector('.js-site-menu-body');
  const rootLevel = menu.querySelector('[data-site-menu-root]');
  const back = menu.querySelector('.js-site-menu-back');
  const backLabel = menu.querySelector('.js-site-menu-back-label');
  const desktop = window.matchMedia('(min-width: 1200px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const slideClasses = ['site-menu__level--from-end', 'site-menu__level--from-start'];
  const topLayer = typeof menu.showPopover === 'function' && menu.hasAttribute('popover');
  // Each step remembers the level it left and the row that opened the next one.
  const trail = [];
  let current = rootLevel;

  function focusables() {
    return Array.from(panel.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'))
      .filter((element) => element.getClientRects().length > 0);
  }

  function show(level, direction) {
    current.hidden = true;
    current.classList.remove(...slideClasses);
    level.classList.remove(...slideClasses);
    level.hidden = false;
    if (direction && !reducedMotion.matches) level.classList.add('site-menu__level--from-' + direction);
    current = level;
    const nested = level !== rootLevel;
    menu.classList.toggle('site-menu--nested', nested);
    if (nested) backLabel.textContent = level.dataset.title || '';
    body.scrollTop = 0;
  }

  function openLevel(opener) {
    const level = document.getElementById(opener.getAttribute('aria-controls'));
    if (!level) return;
    opener.setAttribute('aria-expanded', 'true');
    trail.push({ level: current, opener });
    show(level, 'end');
    back.focus();
  }

  function goBack() {
    const step = trail.pop();
    if (!step) return;
    step.opener.setAttribute('aria-expanded', 'false');
    show(step.level, 'start');
    step.opener.focus();
  }

  function reset() {
    for (const step of trail) step.opener.setAttribute('aria-expanded', 'false');
    trail.length = 0;
    if (current !== rootLevel) show(rootLevel);
  }

  function open() {
    if (desktop.matches || !menu.hidden) return;
    reset();
    menu.hidden = false;
    if (topLayer) menu.showPopover();
    document.documentElement.classList.add('is-locked');
    toggle.setAttribute('aria-expanded', 'true');
    (rootLevel.querySelector('.site-menu__row') || focusables()[0])?.focus();
  }

  function close(returnFocus) {
    if (menu.hidden) return;
    if (topLayer && menu.matches(':popover-open')) menu.hidePopover();
    menu.hidden = true;
    document.documentElement.classList.remove('is-locked');
    toggle.setAttribute('aria-expanded', 'false');
    if (returnFocus) toggle.focus();
  }

  toggle.addEventListener('click', (event) => {
    event.preventDefault();
    open();
  });

  menu.addEventListener('click', (event) => {
    const target = event.target.closest('button, a, .js-site-menu-close');
    if (!target) return;
    if (target.classList.contains('js-site-menu-close')) {
      close(true);
    } else if (target === back) {
      goBack();
    } else if (target.classList.contains('js-site-menu-open')) {
      openLevel(target);
    } else if (target.classList.contains('js-site-menu-lang')) {
      // The header's language button opens drawer-lang through app.js.
      close(false);
      document.querySelector('.site-header .header-main-actions .js-lang-switch')?.click();
    }
  });

  menu.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      close(true);
      return;
    }
    if (event.key !== 'Tab') return;
    const items = focusables();
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && (document.activeElement === first || !panel.contains(document.activeElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  // Esc also closes the menu when focus has left the panel, e.g. after a tap on the backdrop area.
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !menu.hidden && !menu.contains(event.target)) close(true);
  });
  // app.js closes every panel before it opens another one (cart, account, search).
  document.addEventListener('site-header:close-menu', () => close(false));
  desktop.addEventListener('change', () => close(false));
})();
